import { type Device } from "@features/createDeviceConfiguration/graphql/querys/getUpdateHardwareConfigurationData";
import { FormValues } from "@features/createDeviceConfiguration/type/FormValues";
import { Typography, Grid, TextField, MenuItem } from "@mui/material";
import { Controller } from "react-hook-form";
import { Control, useWatch, UseFormSetValue } from "react-hook-form";
import { useEffect, useMemo } from "react";
import OptionSelector from "../../OptionSelector";
import { FormInputText } from "@components/form";
import useHardwareOptions from "@features/createDeviceConfiguration/hooks/useHardwareOptions";
import { ENABLED_SENSOR_TYPES, SENSOR_PROTOCOLS } from "@features/createDeviceConfiguration/data/initialData";

const DOPPLER_MODEL_LABEL = "Doppler LanryBox DOF6000";
const POWER_TYPE_OPTIONS = ['12VDC', '24VDC', '48VDC', '120VDC', '220VAC'];

interface DeviceSensorsFormProps {
  device: Device;
  control: Control<FormValues>;
  setValue: UseFormSetValue<FormValues>;
  readOnly?: boolean;
}

const LABEL_TO_NAME_MAP: Record<string, string> = {
  'Nivel': 'level',
  'Velocidad': 'velocity',
  'Flujo': 'flow',
  'Inclinómetro': 'angle',
  'Potenciómetro': 'potentiometer',
};

const DeviceSensorsForm = ({
  device,
  control,
  setValue,
  readOnly = false,
}: DeviceSensorsFormProps) => {
  const childDevice = device?.relatedDevices?.find(rel => rel.relation === 'CHILD')?.device;
  const isParentDevice = !!childDevice || readOnly;
  // Watch all sensor model options to conditionally render serial number fields
  const watchedSensors = useWatch({
    control,
    name: 'sensors',
    defaultValue: [],
  });

  // Identify level and velocity sensor indices
  const levelSensorIndex = useMemo(() =>
    (device.sensors ?? [])
      .filter(sensor => ENABLED_SENSOR_TYPES.includes(sensor.type.label ?? ""))
      .findIndex(sensor => sensor.type.label === 'Nivel'),
    [device.sensors]
  );

  const velocitySensorIndex = useMemo(() =>
    (device.sensors ?? [])
      .filter(sensor => ENABLED_SENSOR_TYPES.includes(sensor.type.label ?? ""))
      .findIndex(sensor => sensor.type.label === 'Velocidad'),
    [device.sensors]
  );


  // Check if Doppler model is selected for level sensor
  const isDopplerSelected = useMemo(() => {
    if (levelSensorIndex === -1) return false;
    const levelSensorModel = watchedSensors?.[levelSensorIndex]?.sensorModelOption;
    return levelSensorModel?.name === DOPPLER_MODEL_LABEL;
  }, [watchedSensors, levelSensorIndex]);

  // Get velocity sensor options for proper object reference
  const velocitySensorOptions = useHardwareOptions('velocity_sensor').options;

  // Sync level sensor data to velocity sensor when Doppler is selected
  useEffect(() => {
    if (!isDopplerSelected || levelSensorIndex === -1 || velocitySensorIndex === -1) return;

    const levelSensor = watchedSensors?.[levelSensorIndex];
    if (!levelSensor) return;

    type SensorField = keyof FormValues['sensors'][number];
    // Fields to sync from level to velocity (excluding sensorModelOption which needs special handling)
    const fieldsToSync: SensorField[] = [
      'serialNumber',
      'modbusId',
      'batchNumber',
      'integrationDetail',
      'attributes'
    ];

    // Sync regular fields
    fieldsToSync.forEach(field => {
      const levelValue = levelSensor[field];
      setValue(`sensors.${velocitySensorIndex}.${field}` as any, levelValue, {
        shouldDirty: true,
        shouldValidate: false,
      });
    });

    // Special handling for sensorModelOption: find the correct object from velocity options by name
    const levelSensorModelOption = levelSensor.sensorModelOption;
    if (levelSensorModelOption) {
      const matchingOption = velocitySensorOptions.find(
        opt => opt.name === levelSensorModelOption.name
      );
      if (matchingOption) {
        setValue(`sensors.${velocitySensorIndex}.sensorModelOption` as any, matchingOption, {
          shouldDirty: true,
          shouldValidate: false,
        });
      }
    }
  }, [isDopplerSelected, levelSensorIndex, velocitySensorIndex, watchedSensors, velocitySensorOptions, setValue]);

  // Clear velocity data when Doppler is deselected (excluding factorM and offsetN)
  useEffect(() => {
    if (isDopplerSelected || velocitySensorIndex === -1) return;

    const velocitySensor = watchedSensors?.[velocitySensorIndex];
    const velocityHadDoppler = velocitySensor?.sensorModelOption?.name === DOPPLER_MODEL_LABEL;

    if (velocityHadDoppler) {
      const fieldsToSync = [
        'sensorModelOption',
        'serialNumber',
        'modbusId',
        'batchNumber',
        'integrationDetail',
        'attributes'
      ] as const;

      fieldsToSync.forEach(field => {
        setValue(`sensors.${velocitySensorIndex}.${field}` as any, null, {
          shouldDirty: true,
          shouldValidate: false
        });
      });
    }
  }, [isDopplerSelected, velocitySensorIndex, watchedSensors, setValue]);

  // Clear integrationDetail when sensorModelOption changes and is not "integration"
  useEffect(() => {
    watchedSensors?.forEach((sensor, index) => {
      const isIntegration = sensor?.sensorModelOption?.name === 'Integración';
      if (!isIntegration && sensor?.integrationDetail) {
        setValue(`sensors.${index}.integrationDetail` as any, null, { shouldDirty: true });
      }
    });
  }, [watchedSensors, setValue]);

  // Clear powerType when sensor is no longer integration or poweredBy is not 'Nosotros'
  useEffect(() => {
    watchedSensors?.forEach((sensor, index) => {
      const isIntegration = sensor?.sensorModelOption?.name === 'Integración';
      const poweredByNosotros = sensor?.attributes?.poweredBy === 'Nosotros';
      if ((!isIntegration || !poweredByNosotros) && sensor?.attributes?.powerType) {
        setValue(`sensors.${index}.attributes` as any, {
          ...(sensor.attributes ?? {}),
          powerType: null,
        }, { shouldDirty: true });
      }
    });
  }, [watchedSensors, setValue]);
  const attributesFMX = [
    { key: "measurementRange", label: "Rango de medición" },
    { key: "cableLength", label: "Largo de cable (m)" },
    { key: "depth", label: "Profundidad (cm)" },
    { key: "fmx21Code", label: "Código FMX21" }
  ];

  const attributesFlowmeter = [
    { key: "pulseValue", label: "Valor de pulso" },
    { key: "pulseWidth", label: "Ancho del pulso" }
  ];
  return (
    <Grid container spacing={2} sx={{ justifyContent: 'center' }}>
      {(device.sensors ?? []).filter(sensor => ENABLED_SENSOR_TYPES.includes(sensor.type.label ?? "")).map((sensor, index) => {
        const sensorTypeName = LABEL_TO_NAME_MAP[sensor.type.label ?? ""] ?? sensor.type.name;

        // Determine if integration detail should be shown for this sensor
        const currentSensorModel = watchedSensors?.[index]?.sensorModelOption;
        const isIntegration = currentSensorModel?.name === 'Integración';
        const isFMX = currentSensorModel?.name === 'FMX21';
        const poweredByNosotros = watchedSensors?.[index]?.attributes?.poweredBy === 'Nosotros';

        // Get protocol for this sensor
        const protocol = watchedSensors?.[index]?.attributes?.protocol ?? null;

        // Check if this is the velocity sensor and Doppler is selected for level
        const isVelocitySensor = sensor.type.label === 'Velocidad';
        const shouldDisableVelocity = isVelocitySensor && isDopplerSelected;

        // Create a unique key for velocity sensor that changes when synchronized values change
        // Exclude factorM and offsetN from the key since they're editable
        const sensorKey = isVelocitySensor && isDopplerSelected
          ? `${index}-${JSON.stringify({
              sensorModelOption: watchedSensors?.[index]?.sensorModelOption,
              serialNumber: watchedSensors?.[index]?.serialNumber,
              modbusId: watchedSensors?.[index]?.modbusId,
              batchNumber: watchedSensors?.[index]?.batchNumber,
              integrationDetail: watchedSensors?.[index]?.integrationDetail,
              attributes: watchedSensors?.[index]?.attributes
            })}`
          : index;

        return (
        <Grid item xs={12} md={6} key={sensorKey}>
          <Typography variant="h6" color="textSecondary" mt={2} ml={1}>
            Sensor {index + 1} de {sensor?.type?.label}{sensor.childDeviceId && sensor.childDeviceId !== device.id  ? ` - ID ${sensor.childDeviceId}` : ''}
            {shouldDisableVelocity && (
              <Typography component="span" variant="caption" color="info.main" sx={{ ml: 1 }}>
                (Sincronizado con sensor de nivel)
              </Typography>
            )}
          </Typography>
          <Grid item xs={12} mt={2}>
            <OptionSelector
              isDisabled={isParentDevice || shouldDisableVelocity}
              options={
                useHardwareOptions(`${sensorTypeName}_sensor`).options
              }
              label="Tipo de sensor *"
              name={`sensors.${index}.sensorModelOption`}
              control={control}
            />
          </Grid>
          <Grid item xs={12} mt={2}>
            <Controller
              name={`sensors.${index}.attributes.protocol` as any}
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  fullWidth
                  label="Protocolo"
                  disabled={isParentDevice || shouldDisableVelocity}
                  value={field.value ?? ''}
                >
                  {SENSOR_PROTOCOLS.map((protocol) => (
                    <MenuItem key={protocol} value={protocol}>{protocol}</MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Grid>
          {isIntegration && (
            <Grid item xs={12} mt={2}>
              <FormInputText
                disabled={isParentDevice || shouldDisableVelocity}
                name={`sensors.${index}.integrationDetail`}
                control={control}
                label="Detalle de integración"
              />
            </Grid>
          )}
          {isIntegration && (
            <Grid item xs={12} mt={2}>
              <Controller
                name={`sensors.${index}.attributes.poweredBy` as any}
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    label="Alimentado por"
                    disabled={isParentDevice || shouldDisableVelocity}
                    value={field.value ?? ''}
                  >
                    <MenuItem value="Nosotros">Nosotros</MenuItem>
                    <MenuItem value="Cliente">Cliente</MenuItem>
                  </TextField>
                )}
              />
            </Grid>
          )}
          {isIntegration && poweredByNosotros && (
            <Grid item xs={12} mt={2}>
              <Controller
                name={`sensors.${index}.attributes.powerType` as any}
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    label="Tipo de energía alimentado"
                    disabled={isParentDevice || shouldDisableVelocity}
                    value={field.value ?? ''}
                  >
                    {POWER_TYPE_OPTIONS.map(opt => (
                      <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
          )}
          {isFMX && attributesFMX.map(attr => (
            <Grid item xs={12} mt={2} key={attr.key}>
              <FormInputText
                disabled={isParentDevice || shouldDisableVelocity}
                name={`sensors.${index}.attributes.${attr.key}`}
                control={control}
                label={attr.label}
              />
            </Grid>
          ))}
          <Grid item xs={12} mt={2}>
            <FormInputText
              disabled={isParentDevice || shouldDisableVelocity}
              name={`sensors.${index}.serialNumber`}
              control={control}
              label={isIntegration ? "Número de serie del sensor" : "Número de serie del sensor *"}
            />
          </Grid>
          {!isIntegration && protocol !== '4-20mA' && (
            <Grid item xs={12} mt={2}>
              <FormInputText
                disabled={isParentDevice || shouldDisableVelocity}
                name={`sensors.${index}.modbusId`}
                control={control}
                label="Modbus ID del sensor"
              />
            </Grid>
          )}
          {!isIntegration && (
            <Grid item xs={12} mt={2}>
              <FormInputText
                disabled={isParentDevice || shouldDisableVelocity}
                name={`sensors.${index}.batchNumber`}
                control={control}
                label="Número de lote del sensor"
              />
            </Grid>
          )}
          <Grid item xs={12} mt={2}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormInputText
                  disabled={isParentDevice}
                  name={`sensors.${index}.factorM`}
                  control={control}
                  label="Factor M"
                  type="number"
                />
              </Grid>
              <Grid item xs={6}>
                <FormInputText
                  disabled={isParentDevice}
                  name={`sensors.${index}.offsetN`}
                  control={control}
                  label="Offset N"
                  type="number"
                />
              </Grid>
            </Grid>
          </Grid>
          {sensor.type.label === "Flujo" && (
            <Grid item xs={12} mt={2}>
              <Grid container spacing={2}>
                {attributesFlowmeter.map(attr => (
                <Grid item xs={6} key={attr.key}>
                  <FormInputText
                    disabled={isParentDevice || shouldDisableVelocity}
                    name={`sensors.${index}.attributes.${attr.key}`}
                    control={control}
                    label={attr.label}
                  />
                </Grid>
              ))}
              </Grid>
            </Grid>
          )}
        </Grid>
      )})}
    </Grid>
  );
}

export default DeviceSensorsForm;
